import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Calendar2, CalendarTick, Briefcase, Clock, Edit2 } from "iconsax-react";
import { CheckIcon } from "@/shared/assets/icons";
import { extractErrorMessage } from "@/shared/utils";
import { CloseIcon } from "@/shared/assets/icons";
import { useUpdateWorkTimeBalanceMutation } from "../../../api";
import type { UpdateWorkTimeBalanceDto } from "../../../types";
import { Button } from "@/shared/ui";

interface Props {
  timesheetId: number;
  calendarDays: number;
  holidayDays: number;
  workDays: number;
  workHours: number;
  canEdit: boolean;
  onSaveSuccess: (title: string, text: string) => void;
  onSaveError: (title: string, text: string) => void;
}

type FieldKey = keyof UpdateWorkTimeBalanceDto;

export default function TimesheetMonthStats({
  timesheetId,
  calendarDays,
  holidayDays,
  workDays,
  workHours,
  canEdit,
  onSaveSuccess,
  onSaveError,
}: Props) {
  const { t } = useTranslation("TimesheetsPage");
  const [updateWorkTimeBalance] = useUpdateWorkTimeBalanceMutation();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const cards = [
    {
      label: t("detail.calendarDays"),
      value: calendarDays,
      icon: Calendar2,
      fieldKey: "total_calendar_days" as FieldKey,
      isInteger: true,
    },
    {
      label: t("detail.holidayDays"),
      value: holidayDays,
      icon: CalendarTick,
      fieldKey: "month_holiday_days" as FieldKey,
      isInteger: true,
    },
    {
      label: t("detail.workDays"),
      value: workDays,
      icon: Briefcase,
      fieldKey: "month_work_days" as FieldKey,
      isInteger: true,
    },
    {
      label: t("detail.workHours40h"),
      value: workHours,
      icon: Clock,
      fieldKey: "month_work_hours_8h" as FieldKey,
      isInteger: false,
    },
  ];

  useEffect(
    function focusInputOnEdit() {
      if (editingIndex !== null && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    },
    [editingIndex],
  );

  function handleEditClick(index: number) {
    setEditingIndex(index);
    setEditValue(String(parseFloat(String(cards[index].value))));
  }

  function handleCancel() {
    setEditingIndex(null);
    setEditValue("");
  }

  async function handleSave(index: number) {
    const card = cards[index];
    const numValue = Number(editValue);

    if (isNaN(numValue) || numValue < 0) {
      onSaveError(t("messages.errorTitle"), t("workTimeBalance.errors.mustBeNonNegative"));
      return;
    }
    if (card.isInteger && !Number.isInteger(numValue)) {
      onSaveError(t("messages.errorTitle"), t("workTimeBalance.errors.mustBeInteger"));
      return;
    }
    if (numValue === card.value) {
      setEditingIndex(null);
      return;
    }
    if (isSaving) return;

    try {
      setIsSaving(true);
      await updateWorkTimeBalance({
        id: timesheetId,
        body: { [card.fieldKey]: numValue },
      }).unwrap();
      setEditingIndex(null);
      onSaveSuccess(t("workTimeBalance.messages.updateSuccessTitle"), t("workTimeBalance.messages.updateSuccessText"));
    } catch (err: unknown) {
      onSaveError(t("messages.errorTitle"), extractErrorMessage(err, "workTimeBalance.messages.updateErrorText", t));
    } finally {
      setIsSaving(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent, index: number) {
    if (e.key === "Enter") {
      handleSave(index);
    } else if (e.key === "Escape") {
      handleCancel();
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      setEditValue(val);
    }
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
      {cards.map(function renderCard(card, i) {
        const Icon = card.icon;
        const isEditing = editingIndex === i;

        return (
          <div key={i} className="surface-container-fill radius-3xl p-5 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 aspect-square flex justify-center items-center surface-component-fill content-action-neutral radius-xs shrink-0">
                <Icon size={16} color="currentColor" />
              </div>
              <p className="text-label-md content-base-primary flex-1 min-w-0">{card.label}</p>
              {canEdit && !isEditing && (
                <Button
                  variant="secondary"
                  onClick={() => handleEditClick(i)}
                  disabled={isSaving || editingIndex !== null}
                  className="w-5! p-0! aspect-square flex justify-center items-center content-base-secondary cursor-pointer rounded-sm! shrink-0">
                  <Edit2 size={12} color="currentColor" />
                </Button>
              )}
              {isEditing && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="secondary"
                    onClick={() => handleSave(i)}
                    disabled={isSaving}
                    className="w-5! p-0! aspect-square flex justify-center items-center content-base-secondary cursor-pointer rounded-sm! shrink-0">
                    <CheckIcon />
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="w-5! p-0! aspect-square flex justify-center items-center content-base-secondary cursor-pointer rounded-sm! shrink-0">
                    <CloseIcon size={12} />
                  </Button>
                </div>
              )}
            </div>

            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={editValue}
                onChange={handleInputChange}
                onKeyDown={(e) => handleKeyDown(e, i)}
                disabled={isSaving}
                className="text-display-sm content-base-primary bg-transparent border-b-2 border-black/20 dark:border-white/20 outline-none w-full"
              />
            ) : (
              <h6 className="text-display-sm content-base-primary">
                {Number.isInteger(card.value) ? card.value : parseFloat(String(card.value))}
              </h6>
            )}
          </div>
        );
      })}
    </div>
  );
}

